import { useMemo, useState } from "react";
import DataEntryKpiSchoolsModal from "@/components/DataEntryKpiSchoolsModal";
import HeroStatusChip from "@/components/HeroStatusChip";
import {
  AlertCircle,
  AlertTriangle,
  ShieldCheck,
  TrendingUp,
} from "lucide-react";
import { BlockLink } from "@/components/DrilldownLink";
import DataEntryGuidanceChip from "@/components/DataEntryGuidanceChip";
import {
  buildAuditTrustGaugeStyle,
  buildBlockAuditRows,
  buildQualityAuditActionQueue,
  buildQualityAuditClosurePlan,
  buildQualityAuditNarrative,
  buildQualityAuditPrimaryAction,
  buildQualityMetricDescription,
  buildQualityCertDonutStyle,
  computeAuditTrustScore,
  fmt,
  fmtLakhs,
  funnelWidthPct,
  getCertificationCoverageBlocks,
  pct,
  titleCase,
} from "@/utils/dataEntryDerived";

const DataEntryQualityAuditTab = ({
  overview,
  dataQuality,
  certificationData,
  blockData,
  topBottomBlocks,
  guidance,
  guidanceProvider,
  guidanceLoading,
  scopeLabel = "Pune District",
}) => {
  const [kpiMetricModal, setKpiMetricModal] = useState(null);
  const auditTrust = computeAuditTrustScore(overview, dataQuality);
  const certRate = certificationData?.certification_rate ?? overview?.certification_rate ?? 0;
  const nonCertSchools =
    certificationData?.non_certified?.schools ?? overview?.non_certified_schools ?? 0;
  const fullEntrySchools = dataQuality?.full_entry_schools ?? overview?.full_completion_schools ?? 0;
  const worstBlock = topBottomBlocks?.worst_block;

  const certCoverageBlocks = useMemo(() => getCertificationCoverageBlocks(blockData, 6), [blockData]);
  const auditRows = useMemo(() => buildBlockAuditRows(blockData), [blockData]);
  const localActionQueue = useMemo(
    () => buildQualityAuditActionQueue(overview, certificationData, dataQuality, blockData),
    [overview, certificationData, dataQuality, blockData]
  );
  const localClosurePlan = useMemo(
    () => buildQualityAuditClosurePlan(certCoverageBlocks),
    [certCoverageBlocks]
  );
  const actionQueue = guidance?.action_queue?.length ? guidance.action_queue : localActionQueue;
  const closurePlan = guidance?.closure_plan?.length ? guidance.closure_plan : localClosurePlan;
  const narrative =
    guidance?.narrative ?? buildQualityAuditNarrative(overview, dataQuality, certRate, nonCertSchools);
  const primaryAction = guidance?.primary_action ?? buildQualityAuditPrimaryAction(overview, certCoverageBlocks);
  const metricDesc = (key) =>
    guidance?.metric_descriptions?.[key] ?? buildQualityMetricDescription(key, dataQuality, overview);

  const scrollToActions = () => {
    document.getElementById("de-quality-actions")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="de-quality-tab" data-testid="data-entry-quality-audit-tab">
      <section className="de-hero-card">
        <div className="de-hero-layout">
          <div>
            <div className="de-score-main">
              <div
                className="de-audit-gauge"
                style={buildAuditTrustGaugeStyle(auditTrust)}
                aria-label={`Audit trust score ${auditTrust} percent`}
              >
                <div>
                  <strong>{pct(auditTrust, 1)}</strong>
                  <span>Audit Trust</span>
                </div>
              </div>
              <div className="de-health-copy">
                <h2>Quality & Audit Command Centre</h2>
                <p>{narrative}</p>
                <div className="de-status-row">
                  <DataEntryGuidanceChip provider={guidanceProvider} loading={guidanceLoading} />
                  <HeroStatusChip chipClass="de-status-chip" variant="teal">
                    {pct(dataQuality?.consistency_rate ?? 0, 1)} consistency
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="de-status-chip"
                    variant="blue"
                    onClick={() => scrollToActions()}
                    ariaLabel="Name mismatches — view quality audit action queue"
                  >
                    {fmt(dataQuality?.mismatch_schools ?? 0)} mismatches
                  </HeroStatusChip>
                  <HeroStatusChip chipClass="de-status-chip" variant="amber">
                    {fmt(fullEntrySchools)} full-entry schools
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="de-status-chip"
                    variant="red"
                    onClick={() => setKpiMetricModal("non_certified")}
                    ariaLabel={`${nonCertSchools} non-certified schools — view awaiting certification`}
                  >
                    {fmt(nonCertSchools)} non-certified
                  </HeroStatusChip>
                </div>
              </div>
            </div>
            <div className="de-summary-note">
              <div className="de-icon-box orange">
                <AlertTriangle size={22} />
              </div>
              <div>
                <b>Primary audit action</b>
                <span>{primaryAction}</span>
              </div>
            </div>
          </div>

          <div className="de-hero-kpis" aria-label="Quality audit KPIs">
            <article className="de-hero-kpi success">
              <small>Data consistency</small>
              <strong>{pct(dataQuality?.consistency_rate ?? 0, 1)}</strong>
              <span>{fmt(dataQuality?.consistent_schools ?? 0)} schools without mismatch</span>
            </article>
            <article className="de-hero-kpi">
              <small>Total students covered</small>
              <strong>{fmtLakhs(overview?.total_students ?? 0)}</strong>
              <span>School-wise entries monitored</span>
            </article>
            <article className="de-hero-kpi danger">
              <small>Repeaters flagged</small>
              <strong>{fmt(overview?.total_repeaters ?? 0)}</strong>
              <span>{pct(overview?.repeater_rate ?? 0, 2)} repeater rate</span>
            </article>
            <article className="de-hero-kpi warn">
              <small>Certification rate</small>
              <strong>{pct(certRate, 1)}</strong>
              <span>
                {fmt(certificationData?.certified?.schools ?? overview?.certified_schools ?? 0)} of{" "}
                {fmt(overview?.total_schools ?? 0)} schools certified
              </span>
            </article>
          </div>
        </div>
      </section>

      <section className="de-quality-metrics">
        <article className="de-quality-metric green">
          <small>Data Consistency</small>
          <strong>{pct(dataQuality?.consistency_rate ?? 0, 1)}</strong>
          <span>{metricDesc("consistency")}</span>
        </article>
        <article className="de-quality-metric red">
          <small>Data Mismatch</small>
          <strong>{fmt(dataQuality?.mismatch_schools ?? 0)}</strong>
          <span>{metricDesc("mismatch")}</span>
        </article>
        <article className="de-quality-metric orange">
          <small>Zero Entry Schools</small>
          <strong>{fmt(dataQuality?.zero_entry_schools ?? 0)}</strong>
          <span>{metricDesc("zero_entry")}</span>
        </article>
        <article className="de-quality-metric purple">
          <small>Full Entry Schools</small>
          <strong>{fmt(fullEntrySchools)}</strong>
          <span>{metricDesc("full_entry")}</span>
        </article>
      </section>

      <section className="de-two-column">
        <article className="de-panel">
          <div className="de-panel-head">
            <div>
              <h3>Audit Readiness Funnel</h3>
              <p>Completion is strong, but the audit-ready layer narrows sharply at certification.</p>
            </div>
            <span className="de-pill green">Validation path</span>
          </div>
          <div className="de-funnel">
            <div className="de-funnel-step">
              <div className="de-funnel-label">Students covered</div>
              <div className="de-funnel-track">
                <div className="de-funnel-fill blue" style={{ width: "100%" }} />
              </div>
              <div className="de-funnel-value">{fmtLakhs(overview?.total_students ?? 0)}</div>
            </div>
            <div className="de-funnel-step">
              <div className="de-funnel-label">Data completed</div>
              <div className="de-funnel-track">
                <div
                  className="de-funnel-fill green"
                  style={{ width: `${Math.min(overview?.completion_rate ?? 0, 100)}%` }}
                />
              </div>
              <div className="de-funnel-value">{pct(overview?.completion_rate ?? 0, 2)}</div>
            </div>
            <div className="de-funnel-step">
              <div className="de-funnel-label">Schools fully entered</div>
              <div className="de-funnel-track">
                <div
                  className="de-funnel-fill green"
                  style={{
                    width: `${funnelWidthPct(fullEntrySchools, overview?.total_schools ?? 1)}%`,
                  }}
                />
              </div>
              <div className="de-funnel-value">{fmt(fullEntrySchools)}</div>
            </div>
            <div className="de-funnel-step">
              <div className="de-funnel-label">Certified schools</div>
              <div className="de-funnel-track">
                <div
                  className="de-funnel-fill red"
                  style={{ width: `${Math.max(certRate, 4)}%` }}
                />
              </div>
              <div className="de-funnel-value">{pct(certRate, 1)}</div>
            </div>
          </div>
        </article>

        <article className="de-panel">
          <div className="de-panel-head">
            <div>
              <h3>Certification Bottleneck</h3>
              <p>Only a small share of schools have reached formal sign-off despite near-complete data entry.</p>
            </div>
            <span className="de-pill red">Needs closure</span>
          </div>
          <div className="de-qa-cert-layout">
            <div
              className="de-qa-donut"
              style={buildQualityCertDonutStyle(certRate)}
              aria-label="Certification donut"
            />
            <div className="de-cert-stack">
              <div className="de-cert-card green">
                <small>Certified schools</small>
                <strong>{fmt(certificationData?.certified?.schools ?? 0)}</strong>
                <span>
                  {(certificationData?.certified?.students ?? 0).toLocaleString("en-IN")} students covered by
                  certified records
                </span>
              </div>
              <div className="de-cert-card red">
                <small>Non-certified schools</small>
                <strong>{fmt(certificationData?.non_certified?.schools ?? 0)}</strong>
                <span>
                  {(certificationData?.non_certified?.students ?? 0).toLocaleString("en-IN")} students still
                  awaiting formal certification
                </span>
              </div>
            </div>
          </div>
        </article>
      </section>

      {worstBlock && (
        <section className="de-alert-card">
          <div className="de-icon-box red">
            <AlertCircle size={22} />
          </div>
          <div>
            <h3>Escalation Alert: Worst Performing Block</h3>
            <p>
              <strong>{titleCase(worstBlock.block_name)}</strong> has the lowest completion rate at{" "}
              <strong>{pct(worstBlock.completion_rate, 2)}</strong>. It is technically high, but should still
              be tracked because it represents the district&apos;s weakest completion point.
            </p>
          </div>
          <div className="de-alert-stat">
            <strong>{fmt(worstBlock.total_schools)}</strong>
            <span>
              schools · {fmt(worstBlock.total_students)} students
            </span>
          </div>
        </section>
      )}

      <div className="de-section-title">
        <div>
          <h2>Quality Risk Diagnostics</h2>
          <p>
            Instead of one large chart, the redesigned view breaks risk into certification coverage, repeater
            concentration, and block-level audit readiness.
          </p>
        </div>
        <button type="button" className="de-section-action" onClick={scrollToActions}>
          Go to action queue →
        </button>
      </div>

      <section className="de-two-column">
        <article className="de-panel">
          <div className="de-panel-head">
            <div>
              <h3>Certification Coverage by Block</h3>
              <p>Blocks below 10% certification should be moved to officer review.</p>
            </div>
            <span className="de-pill red">Low sign-off</span>
          </div>
          <div className="de-bar-stack">
            {certCoverageBlocks.map((block) => {
              const cert = block.certification_rate ?? 0;
              const barTone = cert >= 10 ? "" : "red";
              return (
                <div className="de-bar-row" key={block.block_name}>
                  <div className="de-bar-label">{titleCase(block.block_name)}</div>
                  <div className="de-bar-track">
                    <div
                      className={`de-cert-bar ${barTone}`}
                      style={{ width: `${Math.max(cert, 3)}%` }}
                    />
                  </div>
                  <div className="de-bar-value">{pct(cert, 1)}</div>
                </div>
              );
            })}
          </div>
        </article>

        <article className="de-panel">
          <div className="de-panel-head">
            <div>
              <h3>Audit Exception Buckets</h3>
              <p>Quick separation of clean data, certification gaps, and repeaters for faster review.</p>
            </div>
            <span className="de-pill amber">Exception view</span>
          </div>
          <div className="de-risk-grid">
            <div className="de-risk-card green">
              <h4>Clean structural data</h4>
              <strong>{pct(dataQuality?.consistency_rate ?? 0, 1)}</strong>
              <p>No data mismatch or zero-entry school issue reported.</p>
            </div>
            <div className="de-risk-card red">
              <h4>Certification backlog</h4>
              <strong>{fmt(nonCertSchools)}</strong>
              <p>Largest audit risk is sign-off, not data entry.</p>
            </div>
            <div className="de-risk-card orange">
              <h4>Repeater cases</h4>
              <strong>{fmt(overview?.total_repeaters ?? 0)}</strong>
              <p>Needs school-level validation before final reporting.</p>
            </div>
            <div className="de-risk-card blue">
              <h4>Full-entry base</h4>
              <strong>{fmt(fullEntrySchools)}</strong>
              <p>Schools that can be moved into certification queue.</p>
            </div>
          </div>
        </article>
      </section>

      <section className="de-two-column">
        <article className="de-panel de-table-panel">
          <div className="de-table-head">
            <div>
              <h3>Block Audit Prioritisation</h3>
              <p>Blocks sorted by operational risk and certification closure requirement.</p>
            </div>
            <span className="de-pill red">Officer focus</span>
          </div>
          <div className="de-table-wrap">
            <table className="de-table" aria-label="Block audit prioritisation">
              <thead>
                <tr>
                  <th>Priority</th>
                  <th>Block</th>
                  <th className="de-num">Completion</th>
                  <th className="de-num">Certified</th>
                  <th className="de-num">Repeaters</th>
                  <th>Risk Signal</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {auditRows.map((block) => (
                  <tr key={block.block_name}>
                    <td>
                      <span className={`de-badge ${block.priority.tone}`}>{block.priority.label}</span>
                    </td>
                    <td>
                      <BlockLink blockCode={block.block_code} blockName={block.block_name}>
                        {block.block_name}
                      </BlockLink>
                    </td>
                    <td className="de-num">{pct(block.completion_pct ?? block.completion_rate ?? 0, 2)}</td>
                    <td className="de-num">{pct(block.certification_rate ?? 0, 1)}</td>
                    <td className="de-num">{fmt(block.total_repeaters ?? 0)}</td>
                    <td>
                      <span className={`de-badge ${block.risk.tone}`}>{block.risk.label}</span>
                    </td>
                    <td>
                      <span className="de-action-link">{block.action}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </article>

        <article className="de-panel" id="de-quality-actions">
          <div className="de-panel-head">
            <div>
              <h3>Officer Action Queue</h3>
              <p>Action cards convert audit findings into clear ownership and due dates.</p>
            </div>
            <span className="de-pill green">Ready to assign</span>
          </div>
          <div className="de-quality-action-list">
            {actionQueue.map((item) => (
              <div className="de-quality-action-item" key={item.title}>
                <div className={`de-icon-box ${item.tone}`}>
                  {item.tone === "red" ? (
                    <AlertTriangle size={22} />
                  ) : item.tone === "orange" ? (
                    <TrendingUp size={22} />
                  ) : (
                    <ShieldCheck size={22} />
                  )}
                </div>
                <div>
                  <h4>{item.title}</h4>
                  <p>{item.body}</p>
                </div>
                <span className="de-due">{item.due}</span>
              </div>
            ))}
          </div>
        </article>
      </section>

      <section className="de-panel">
        <div className="de-panel-head">
          <div>
            <h3>30-Day Quality & Audit Closure Plan</h3>
            <p>
              A practical sprint plan for district officers to move from data completion to certified,
              audit-ready reporting.
            </p>
          </div>
          <span className="de-pill blue">30-day plan</span>
        </div>
        <div className="de-phase-timeline">
          {closurePlan.map((step) => (
            <div className="de-phase" key={step.phase}>
              <small>{step.phase}</small>
              <h4>{step.title}</h4>
              <p>{step.text}</p>
            </div>
          ))}
        </div>
      </section>

      <DataEntryKpiSchoolsModal
        open={Boolean(kpiMetricModal)}
        metric={kpiMetricModal}
        onClose={() => setKpiMetricModal(null)}
        scopeLabel={scopeLabel}
        districtPct={kpiMetricModal === "non_certified" ? certRate : null}
      />
    </div>
  );
};

export default DataEntryQualityAuditTab;
